import type { CustomEmojiListing } from "@beacon/core/custom-emoji"
import { emojiName } from "@beacon/core/custom-emoji/names"
import { useQueryClient } from "@tanstack/react-query"
import { ImagePlusIcon, Loader2Icon } from "lucide-react"
import { useEffect, useId, useRef, useState, type FormEvent } from "react"
import { toast } from "sonner"
import { refusalOf } from "@/administration/client.ts"
import { Kbd } from "@/design-system/index.ts"
import { Button } from "@/primitives/button.tsx"
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/primitives/dialog.tsx"
import { Field, FieldDescription, FieldError, FieldLabel, FieldTitle } from "@/primitives/field.tsx"
import { Input } from "@/primitives/input.tsx"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import { describeCustomEmojiRefusal, nameFromFile, namingProblem } from "./custom-describe.ts"
import { addCustomEmoji } from "./custom.ts"

/** The pictures the file picker offers: those the web API draws an emoji from. */
const accepted = "image/png,image/jpeg,image/gif,image/webp"

/** A file chosen, shown from this device until it is added. */
function useLocalPreview(file: File | undefined) {
  const [url, setUrl] = useState<string>()
  useEffect(() => {
    if (!file) return setUrl(undefined)
    const made = URL.createObjectURL(file)
    setUrl(made)
    return () => URL.revokeObjectURL(made)
  }, [file])
  return url
}

/*
 * Add emoji: any Member adds a picture to the company's custom emoji, named as people will type it
 * between colons, for everyone to use in messages and reactions. The name starts from the file's,
 * and says what is wrong with it as it is typed; the web API draws the picture again as a square,
 * so a PNG, JPEG, GIF (animated or not) or WebP of up to 2 MB will do. Only an Admin renames or
 * removes one afterwards, which the dialog says.
 */
export function AddCustomEmoji({
  open,
  onOpenChange,
  onAdded,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdded?: (emoji: CustomEmojiListing) => void
}) {
  const [file, setFile] = useState<File>()
  const [name, setName] = useState("")
  const [pending, setPending] = useState(false)
  const queryClient = useQueryClient()
  const [refused, setRefused] = useState<string>()
  const form = useRef<HTMLFormElement>(null)
  const picker = useRef<HTMLInputElement>(null)
  const nameField = useId()
  const pictureLabel = useId()
  const preview = useLocalPreview(file)

  // Each opening starts afresh.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setFile(undefined)
      setName("")
      setRefused(undefined)
    }
  }

  const problem = name === "" ? undefined : namingProblem(name)
  const named = emojiName(name)
  const canSubmit = file !== undefined && named !== undefined && !pending

  function choose(chosen: File | undefined) {
    if (!chosen) return
    setFile(chosen)
    setRefused(undefined)
    if (name === "") setName(nameFromFile(chosen.name))
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault()
    if (!canSubmit || !file || !named) return
    setPending(true)
    setRefused(undefined)
    try {
      const added = await addCustomEmoji(queryClient, file, named)
      toast.success(`Added :${added.name}:`)
      onAdded?.(added)
      onOpenChange(false)
    } catch (error) {
      setRefused(describeCustomEmojiRefusal(refusalOf(error)))
    } finally {
      setPending(false)
    }
  }
  useShortcut("submit", () => void submit(), { enabled: open, within: form })

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent size="sm">
        <form ref={form} onSubmit={(event) => void submit(event)}>
          <DialogHeader>
            <DialogTitle>Add emoji</DialogTitle>
            <DialogDescription>
              A custom emoji is the whole company&apos;s to use. Only an Admin can rename or remove
              it.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <Field role="group" aria-labelledby={pictureLabel}>
              <FieldTitle id={pictureLabel}>Picture</FieldTitle>
              <input
                ref={picker}
                type="file"
                accept={accepted}
                hidden
                onChange={(event) => {
                  choose(event.target.files?.[0])
                  event.target.value = ""
                }}
              />
              <div className="flex items-center gap-3">
                <div className="flex size-14 shrink-0 items-center justify-center rounded-md border border-border-subtle bg-surface-sunken">
                  {preview ? (
                    <img src={preview} alt="" className="size-10 object-contain" />
                  ) : (
                    <ImagePlusIcon aria-hidden className="size-5 text-subtle-foreground" />
                  )}
                </div>
                <div className="flex min-w-0 flex-col items-start gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    autoFocus
                    onClick={() => picker.current?.click()}
                  >
                    {file ? "Choose another" : "Choose a picture"}
                  </Button>
                  <span className="max-w-full truncate text-xs text-muted-foreground">
                    {file ? file.name : "PNG, JPEG, GIF or WebP, up to 2 MB"}
                  </span>
                </div>
              </div>
            </Field>
            <Field data-invalid={problem !== undefined || undefined}>
              <FieldLabel htmlFor={nameField}>Name</FieldLabel>
              <Input
                id={nameField}
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                placeholder="shipit"
                aria-invalid={problem !== undefined || undefined}
              />
              {problem ? (
                <FieldError>{problem}</FieldError>
              ) : (
                <FieldDescription>
                  {named
                    ? `People type :${named}: to use it.`
                    : "People type its name between colons."}
                </FieldDescription>
              )}
            </Field>
            {refused && <FieldError>{refused}</FieldError>}
          </DialogBody>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />} disabled={pending}>
              Cancel
            </DialogClose>
            <Button type="submit" disabled={!canSubmit} focusableWhenDisabled={pending}>
              {pending && <Loader2Icon data-icon="inline-start" className="animate-spin" />}
              Add
              {!pending && <Kbd shortcut="submit" />}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
